export interface Point {
  x: number;
  y: number;
}

export interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function boundingBoxOf(points: Point[]): BoundingBox {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

export function clampPointToBox(x: number, y: number, box: BoundingBox): Point {
  return {
    x: Math.min(Math.max(x, box.minX), box.maxX),
    y: Math.min(Math.max(y, box.minY), box.maxY),
  };
}

export function clampBoxToBounds(
  x: number,
  y: number,
  width: number,
  height: number,
  box: BoundingBox,
): Point {
  const maxX = Math.max(box.minX, box.maxX - width);
  const maxY = Math.max(box.minY, box.maxY - height);
  return {
    x: Math.min(Math.max(x, box.minX), maxX),
    y: Math.min(Math.max(y, box.minY), maxY),
  };
}

export function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  let t = lengthSquared === 0 ? 0 : ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
  t = Math.min(Math.max(t, 0), 1);
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;
  return Math.hypot(px - closestX, py - closestY);
}

/**
 * Returns the index of the polygon point after which a new vertex should be
 * inserted, i.e. the edge (points[i], points[i+1 mod length]) closest to (px, py).
 */
export function closestEdgeIndex(
  px: number,
  py: number,
  points: Point[],
): { index: number; distance: number } {
  let bestIndex = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const distance = distanceToSegment(px, py, a.x, a.y, b.x, b.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }
  return { index: bestIndex, distance: bestDistance };
}
